import { LitElement, html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';

export type FeaturedIconSize = 'sm' | 'md' | 'lg' | 'xl';
export type FeaturedIconVariant = 'light' | 'gradient' | 'modern' | 'dark' | 'modern-neue';
export type FeaturedIconColor =
  | 'brand'
  | 'gray'
  | 'error'
  | 'warning'
  | 'success'
  | 'plum'
  | 'berry'
  | 'sky';

/**
 * A purely decorative icon container with a styled colored background.
 *
 * Fetches an SVG from `src`, sanitises it to prevent XSS, and injects it
 * inline into the Shadow DOM so that `currentColor` theming drives the icon
 * color. The host is unconditionally `aria-hidden="true"` — consuming pages
 * must provide adjacent accessible context (e.g. a heading or label) for any
 * meaning the icon conveys.
 *
 * Supports four sizes (`sm` | `md` | `lg` | `xl`), five visual style variants
 * (`light` | `gradient` | `modern` | `dark` | `modern-neue`), and eight color
 * themes (`brand` | `gray` | `error` | `warning` | `success` | `plum` | `berry` | `sky`).
 *
 * @element asc-featured-icon
 *
 * @csspart base - Root wrapper. Controls background, border, shape, ring, and shadow.
 * @csspart icon-wrapper - Inner wrapper; transparent passthrough on most variants. On `gradient`, becomes a solid-filled circle behind the icon.
 * @csspart icon - Inner SVG wrapper. Controls icon dimensions and `currentColor`.
 *
 * @cssprop --asc-featured-icon-size - Square container dimensions; varies per `size`.
 * @cssprop --asc-featured-icon-border-radius - Container corner radius.
 * @cssprop --asc-featured-icon-bg - Container background (solid color or gradient); varies per `variant` × `color`.
 * @cssprop --asc-featured-icon-border-color - Container border color; varies per `variant` × `color`.
 * @cssprop --asc-featured-icon-border-width - Container border thickness.
 * @cssprop --asc-featured-icon-icon-color - `color` applied to `[part="icon"]`; injected SVG uses `currentColor`. Varies per `variant` × `color`.
 * @cssprop --asc-featured-icon-icon-size - Injected SVG `width`/`height`; varies per `size`.
 * @cssprop --asc-featured-icon-ring - Complete `box-shadow` ring value for the `modern` variant; varies per `color`.
 * @cssprop --asc-featured-icon-icon-wrapper-bg - Background of the inner circle in the `gradient` variant; varies per `color`.
 */
@customElement('asc-featured-icon')
export class AscFeaturedIcon extends LitElement {
  static override styles = css`
    :host {
      display: inline-flex;
      flex-shrink: 0;
    }

    [part='base'] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: var(--asc-featured-icon-size, var(--asc-spacing-6xl));
      height: var(--asc-featured-icon-size, var(--asc-spacing-6xl));
      border-radius: var(--asc-featured-icon-border-radius, var(--asc-radius-md));
      background: var(--asc-featured-icon-bg, var(--asc-bg-brand-primary));
      border: var(--asc-featured-icon-border-width, var(--asc-border-width)) solid
        var(--asc-featured-icon-border-color, var(--asc-bg-brand-secondary));
      box-shadow: var(--asc-featured-icon-ring, none);
    }

    [part='icon-wrapper'] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    /* gradient only: inner filled circle — 8px smaller than the outer container */
    :host([variant='gradient']) [part='icon-wrapper'] {
      width: calc(var(--asc-featured-icon-size, var(--asc-spacing-6xl)) - 8px);
      height: calc(var(--asc-featured-icon-size, var(--asc-spacing-6xl)) - 8px);
      border-radius: var(--asc-radius-full);
      background: var(--asc-featured-icon-icon-wrapper-bg, var(--asc-bg-brand-secondary));
      flex-shrink: 0;
    }

    /* gradient base: fade the whole element (bg + border) out toward the bottom */
    :host([variant='gradient']) [part='base'] {
      -webkit-mask-image: linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 100%);
      mask-image: linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 100%);
    }

    [part='icon'] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--asc-featured-icon-icon-size, var(--asc-spacing-3xl));
      height: var(--asc-featured-icon-icon-size, var(--asc-spacing-3xl));
      color: var(--asc-featured-icon-icon-color, var(--asc-featured-icon-light-fg-brand));
      flex-shrink: 0;
    }

    [part='icon'] svg {
      width: var(--asc-featured-icon-icon-size, var(--asc-spacing-3xl));
      height: var(--asc-featured-icon-icon-size, var(--asc-spacing-3xl));
    }

    /* ── Border radius per variant ─────────────────────────────────────────── */

    /* light: full circle, no border */
    :host([variant='light']) {
      --asc-featured-icon-border-radius: var(--asc-radius-full);
      --asc-featured-icon-border-width: 0;
    }

    /* gradient: full circle (same as light), 1px solid color border that fades via mask */
    :host([variant='gradient']) {
      --asc-featured-icon-border-radius: var(--asc-radius-full);
      --asc-featured-icon-border-width: 1px;
    }

    /* modern and dark are small rounded squares */
    :host([variant='modern']),
    :host([variant='dark']) {
      --asc-featured-icon-border-radius: var(--asc-radius-xs);
    }

    /* modern-neue outer frame: radius = inner radius-sm (8px) + 4px inset gap + 2px optical */
    :host([variant='modern-neue']) {
      --asc-featured-icon-border-radius: 14px; /* no-token: Figma hardcodes 14px, no variable binding on outer */
    }

    /* ── Size variants ─────────────────────────────────────────────────────── */

    :host([size='sm']) {
      --asc-featured-icon-size: var(--asc-spacing-5xl); /* 40px */
      --asc-featured-icon-icon-size: var(--asc-spacing-2xl); /* 20px */
    }

    :host([size='md']) {
      --asc-featured-icon-size: var(--asc-spacing-6xl); /* 48px */
      --asc-featured-icon-icon-size: var(--asc-spacing-3xl); /* 24px */
    }

    :host([size='lg']) {
      --asc-featured-icon-size: calc(
        var(--asc-spacing-6xl) + var(--asc-spacing-md)
      ); /* 56px: 48+8 */
      --asc-featured-icon-icon-size: calc(
        var(--asc-spacing-3xl) + var(--asc-spacing-xs)
      ); /* 28px: 24+4 */
    }

    :host([size='xl']) {
      --asc-featured-icon-size: var(--asc-spacing-7xl); /* 64px */
      --asc-featured-icon-icon-size: var(--asc-spacing-4xl); /* 32px */
    }

    /* ── Light variant ─────────────────────────────────────────────────────── */

    :host([variant='light'][color='brand']) {
      --asc-featured-icon-bg: var(--asc-bg-brand-primary);
      --asc-featured-icon-icon-color: var(--asc-featured-icon-light-fg-brand);
    }

    :host([variant='light'][color='gray']) {
      --asc-featured-icon-bg: var(--asc-bg-secondary_subtle);
      --asc-featured-icon-icon-color: var(--asc-featured-icon-light-fg-gray);
    }

    :host([variant='light'][color='error']) {
      --asc-featured-icon-bg: var(--asc-bg-error-primary);
      --asc-featured-icon-icon-color: var(--asc-bg-error-solid); /* Colors/Background/bg-error-solid #D2260F */
    }

    :host([variant='light'][color='warning']) {
      --asc-featured-icon-bg: var(--asc-bg-warning-primary);
      --asc-featured-icon-icon-color: var(--asc-bg-warning-solid); /* Colors/Background/bg-warning-solid #C27800 */
    }

    :host([variant='light'][color='success']) {
      --asc-featured-icon-bg: var(--asc-bg-success-primary);
      --asc-featured-icon-icon-color: var(--asc-featured-icon-light-fg-success);
    }

    :host([variant='light'][color='plum']) {
      --asc-featured-icon-bg: var(--asc-accent-plum-bg-hover); /* #E9E9EF */
      --asc-featured-icon-icon-color: var(--asc-accent-plum-fg-hover); /* #524887 */
    }

    :host([variant='light'][color='berry']) {
      --asc-featured-icon-bg: var(--asc-accent-berry-bg-hover); /* #F2E6EB */
      --asc-featured-icon-icon-color: var(--asc-accent-berry-fg-hover); /* #AF4D7E */
    }

    :host([variant='light'][color='sky']) {
      --asc-featured-icon-bg: var(--asc-accent-sky-bg-hover); /* #DBEDF4 */
      --asc-featured-icon-icon-color: var(--asc-accent-sky-fg-hover); /* #037B97 */
    }

    /* ── Gradient variant ──────────────────────────────────────────────────── */

    :host([variant='gradient'][color='brand']) {
      --asc-featured-icon-bg: var(--asc-bg-brand-primary); /* near-white mint, #F1FBF8 */
      --asc-featured-icon-border-color: var(--asc-border-brand);
      --asc-featured-icon-icon-wrapper-bg: var(--asc-bg-brand-secondary);
      --asc-featured-icon-icon-color: var(--asc-fg-brand-primary);
    }

    :host([variant='gradient'][color='gray']) {
      --asc-featured-icon-bg: var(--asc-bg-secondary_subtle); /* near-white gray, #F7F9F9 */
      --asc-featured-icon-border-color: var(--asc-border-primary);
      --asc-featured-icon-icon-wrapper-bg: var(--asc-bg-secondary);
      --asc-featured-icon-icon-color: var(--asc-fg-primary);
    }

    :host([variant='gradient'][color='error']) {
      --asc-featured-icon-bg: var(--asc-bg-error-primary); /* near-white red tint, #FCF7F6 */
      --asc-featured-icon-border-color: var(--asc-border-error_subtle);
      --asc-featured-icon-icon-wrapper-bg: var(--asc-bg-error-secondary);
      --asc-featured-icon-icon-color: var(--asc-fg-error-primary);
    }

    :host([variant='gradient'][color='warning']) {
      --asc-featured-icon-bg: var(--asc-bg-warning-primary); /* near-white amber tint, #FCF8F3 */
      --asc-featured-icon-border-color: var(--asc-border-warning);
      --asc-featured-icon-icon-wrapper-bg: var(--asc-bg-warning-secondary);
      --asc-featured-icon-icon-color: var(--asc-fg-warning-primary);
    }

    :host([variant='gradient'][color='success']) {
      --asc-featured-icon-bg: var(--asc-bg-success-primary); /* near-white green tint, #F3FBF2 */
      --asc-featured-icon-border-color: var(--asc-border-success);
      --asc-featured-icon-icon-wrapper-bg: var(--asc-bg-success-secondary);
      --asc-featured-icon-icon-color: var(--asc-fg-success-primary);
    }

    :host([variant='gradient'][color='plum']) {
      --asc-featured-icon-bg: var(--asc-accent-plum-bg); /* near-white plum tint, #F8F8FA */
      --asc-featured-icon-border-color: var(--asc-accent-plum-border);
      --asc-featured-icon-icon-wrapper-bg: var(--asc-accent-plum-bg-alt);
      --asc-featured-icon-icon-color: var(--asc-accent-plum-text-secondary);
    }

    :host([variant='gradient'][color='berry']) {
      --asc-featured-icon-bg: var(--asc-accent-berry-bg); /* near-white berry tint, #FBF7F9 */
      --asc-featured-icon-border-color: var(--asc-accent-berry-border);
      --asc-featured-icon-icon-wrapper-bg: var(--asc-accent-berry-bg-alt);
      --asc-featured-icon-icon-color: var(--asc-accent-berry-text-secondary);
    }

    :host([variant='gradient'][color='sky']) {
      --asc-featured-icon-bg: var(--asc-accent-sky-bg); /* near-white sky tint, #F5F9FB */
      --asc-featured-icon-border-color: var(--asc-accent-sky-border);
      --asc-featured-icon-icon-wrapper-bg: var(--asc-accent-sky-bg-alt);
      --asc-featured-icon-icon-color: var(--asc-accent-sky-text-secondary);
    }

    /* ── Dark variant ──────────────────────────────────────────────────────── */

    /* All dark variants share the same Bevel/light inner border and skeuomorphic shadow */
    :host([variant='dark']) {
      --asc-featured-icon-border-width: 2px; /* no-token: Figma Bevel border is always 2px */
      --asc-featured-icon-border-color: rgba(255, 255, 255, 0.12); /* no-token: Figma Bevel/light fill style — no exact --asc-alpha-white-* match */
      --asc-featured-icon-ring: 0 0 1px 1px var(--asc-shadow-skeumorphic-inner) inset,
        0 -1px 1px 1px var(--asc-shadow-skeumorphic-inner) inset,
        0 1px 2px 0 var(--asc-shadow-xs);
    }

    :host([variant='dark'][color='brand']) {
      --asc-featured-icon-bg: var(--asc-bg-brand-solid);
      --asc-featured-icon-icon-color: var(--asc-fg-white);
    }

    :host([variant='dark'][color='gray']) {
      --asc-featured-icon-bg: var(--asc-bg-secondary-solid);
      --asc-featured-icon-icon-color: var(--asc-fg-white);
    }

    :host([variant='dark'][color='error']) {
      --asc-featured-icon-bg: var(--asc-bg-error-solid);
      --asc-featured-icon-icon-color: var(--asc-fg-white);
    }

    :host([variant='dark'][color='warning']) {
      --asc-featured-icon-bg: var(--asc-bg-warning-solid);
      --asc-featured-icon-icon-color: var(--asc-fg-white);
    }

    :host([variant='dark'][color='success']) {
      --asc-featured-icon-bg: var(--asc-bg-success-solid);
      --asc-featured-icon-icon-color: var(--asc-fg-white);
    }

    :host([variant='dark'][color='plum']) {
      --asc-featured-icon-bg: var(--asc-accent-plum-bg-strong);
      --asc-featured-icon-icon-color: var(--asc-fg-white);
    }

    :host([variant='dark'][color='berry']) {
      --asc-featured-icon-bg: var(--asc-accent-berry-bg-strong);
      --asc-featured-icon-icon-color: var(--asc-fg-white);
    }

    :host([variant='dark'][color='sky']) {
      --asc-featured-icon-bg: var(--asc-accent-sky-bg-strong);
      --asc-featured-icon-icon-color: var(--asc-fg-white);
    }

    /* ── Modern variant ────────────────────────────────────────────────────── */

    :host([variant='modern']) {
      --asc-featured-icon-bg: var(--asc-bg-primary);
      --asc-featured-icon-border-color: var(--asc-border-tertiary);
    }

    :host([variant='modern'][color='brand']) {
      --asc-featured-icon-ring: 0 0 0 4px var(--asc-bg-brand-primary); /* no-token: 4px ring width is design-system standard */
      --asc-featured-icon-icon-color: var(--asc-featured-icon-light-fg-brand);
    }

    :host([variant='modern'][color='gray']) {
      --asc-featured-icon-ring: 0 0 0 4px var(--asc-bg-secondary_subtle); /* no-token: 4px ring width is design-system standard */
      --asc-featured-icon-icon-color: var(--asc-featured-icon-light-fg-gray);
    }

    :host([variant='modern'][color='error']) {
      --asc-featured-icon-ring: 0 0 0 4px var(--asc-bg-error-primary); /* no-token: 4px ring width is design-system standard */
      --asc-featured-icon-icon-color: var(--asc-bg-error-solid); /* Colors/Background/bg-error-solid #D2260F */
    }

    :host([variant='modern'][color='warning']) {
      --asc-featured-icon-ring: 0 0 0 4px var(--asc-bg-warning-primary); /* no-token: 4px ring width is design-system standard */
      --asc-featured-icon-icon-color: var(--asc-bg-warning-solid); /* Colors/Background/bg-warning-solid #C27800 */
    }

    :host([variant='modern'][color='success']) {
      --asc-featured-icon-ring: 0 0 0 4px var(--asc-bg-success-primary); /* no-token: 4px ring width is design-system standard */
      --asc-featured-icon-icon-color: var(--asc-featured-icon-light-fg-success);
    }

    :host([variant='modern'][color='plum']) {
      --asc-featured-icon-ring: 0 0 0 4px var(--asc-accent-plum-bg-hover); /* no-token: 4px ring width is design-system standard */
      --asc-featured-icon-icon-color: var(--asc-accent-plum-fg-hover);
    }

    :host([variant='modern'][color='berry']) {
      --asc-featured-icon-ring: 0 0 0 4px var(--asc-accent-berry-bg-hover); /* no-token: 4px ring width is design-system standard */
      --asc-featured-icon-icon-color: var(--asc-accent-berry-fg-hover);
    }

    :host([variant='modern'][color='sky']) {
      --asc-featured-icon-ring: 0 0 0 4px var(--asc-accent-sky-bg-hover); /* no-token: 4px ring width is design-system standard */
      --asc-featured-icon-icon-color: var(--asc-accent-sky-fg-hover);
    }

    /* ── Modern Neue variant (gray only) ─────────────────────────────────── */
    /* Figma: thin outer frame + elevated inner card floating 4px inset on all sides */

    :host([variant='modern-neue']) {
      --asc-featured-icon-bg: var(--asc-bg-primary);           /* white outer frame */
      --asc-featured-icon-border-width: 1px;
      --asc-featured-icon-border-color: var(--asc-border-primary); /* Colors/Border/border-primary */
      --asc-featured-icon-icon-color: var(--asc-fg-primary);   /* Colors/Foreground/fg-primary — gray only */
    }

    /* Inner elevated card — 8px smaller than the outer container (4px inset each side) */
    :host([variant='modern-neue']) [part='icon-wrapper'] {
      width: calc(var(--asc-featured-icon-size, var(--asc-spacing-6xl)) - 8px);
      height: calc(var(--asc-featured-icon-size, var(--asc-spacing-6xl)) - 8px);
      border-radius: var(--asc-radius-sm); /* Colors/Border/radius-sm = 8px confirmed by Figma */
      background: var(--asc-bg-primary_alt); /* #F7F9F9 — Colors/Background/bg-primary_alt */
      border: 1px solid var(--asc-border-tertiary); /* Colors/Border/border-tertiary #D3DADD */
      box-shadow: 0 4px 6px -1px var(--asc-shadow-md_01),
        0 2px 4px -2px var(--asc-shadow-md_02); /* Colors/Effects/Shadows/shadow-md */
      overflow: hidden;
      flex-shrink: 0;
    }

    /* modern-neue card themes */
    :host([variant='modern-neue'][color='plum']) [part='icon-wrapper'] {
      background: var(--asc-color-plum-100); /* #E9E9EF */
      border-color: var(--asc-color-plum-800); /* #524887 */
    }

    :host([variant='modern-neue'][color='plum']) {
      --asc-featured-icon-icon-color: var(--asc-color-plum-800); /* #524887 */
    }

    :host([variant='modern-neue'][color='berry']) [part='icon-wrapper'] {
      background: var(--asc-color-berry-100); /* #F2E6EB */
      border-color: var(--asc-color-berry-700); /* #AF4D7E */
    }

    :host([variant='modern-neue'][color='berry']) {
      --asc-featured-icon-icon-color: var(--asc-color-berry-700); /* #AF4D7E */
    }

    :host([variant='modern-neue'][color='sky']) [part='icon-wrapper'] {
      background: var(--asc-color-sky-100); /* #DBEDF4 */
      border-color: var(--asc-color-sky-700); /* #037B97 */
    }

    :host([variant='modern-neue'][color='sky']) {
      --asc-featured-icon-icon-color: var(--asc-color-sky-700); /* #037B97 */
    }
  `;

  /** Controls the container dimensions and icon size. */
  @property({ type: String, reflect: true }) size: FeaturedIconSize = 'md';

  /** Controls the background treatment. Maps to the Figma "Style" variant axis. */
  @property({ type: String, reflect: true }) variant: FeaturedIconVariant = 'light';

  /** Drives the active color theme for background, rings, border, and icon color. */
  @property({ type: String, reflect: true }) color: FeaturedIconColor = 'brand';

  /**
   * URL of the SVG asset (e.g. from Umbraco's media SVG picker).
   * The component fetches, sanitises, and injects the SVG inline.
   * Not reflected to attribute — URL strings should not live as DOM attributes.
   */
  @property({ type: String }) src = '';

  @query('[part="icon"]') private _iconEl!: HTMLDivElement;

  private _abortController: AbortController | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute('aria-hidden', 'true');
    if (this.src) {
      // Defer until after first render so _iconEl is available.
      void this.updateComplete.then(() => this._fetchAndInject(this.src));
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._abortController?.abort();
  }

  override updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('src')) {
      this._abortController?.abort();
      if (this.src) {
        this._fetchAndInject(this.src);
      } else if (this._iconEl) {
        this._iconEl.innerHTML = '';
      }
    }
  }

  private async _fetchAndInject(url: string): Promise<void> {
    this._abortController = new AbortController();
    try {
      const res = await fetch(url, { signal: this._abortController.signal });
      if (!res.ok) return;
      const text = await res.text();
      const sanitised = this._sanitiseSvg(text);
      if (this._iconEl && sanitised) {
        this._iconEl.innerHTML = sanitised;
      }
    } catch {
      // Fetch was aborted or failed — silently clear icon area.
      if (this._iconEl) {
        this._iconEl.innerHTML = '';
      }
    }
  }

  private _sanitiseSvg(raw: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(raw, 'image/svg+xml');

    // DOMParser reports parse errors as an <parsererror> element.
    if (doc.querySelector('parsererror')) return '';

    const svg = doc.querySelector('svg');
    if (!svg) return '';

    // Remove all <script> elements.
    svg.querySelectorAll('script').forEach((el) => el.remove());

    // Remove on* event attributes and javascript: hrefs from every descendant element.
    // Preserve fill/stroke attributes so SVGs that use currentColor continue to work.
    svg.querySelectorAll('*').forEach((el) => {
      [...el.attributes].forEach((attr) => {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
        if (
          (attr.name === 'href' || attr.name === 'xlink:href') &&
          attr.value.trim().toLowerCase().startsWith('javascript:')
        ) {
          el.removeAttribute(attr.name);
        }
      });
    });

    return svg.outerHTML;
  }

  override render() {
    return html`
      <div part="base" role="presentation">
        <div part="icon-wrapper">
          <div part="icon" aria-hidden="true"></div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-featured-icon': AscFeaturedIcon;
  }
}
